import React from "react";
import { useTranslation } from "react-i18next";
import PricingCard from "@/components/pricing/PricingCard";

interface Plan {
  name: string;
  price?: string;
  monthlyPrice?: string;
  annualPrice?: string;
  subtitle?: string;
  description?: string;
  features: string[];
  popular: boolean;
  buttonText: string;
  icon: string;
  upgradeNote?: string;
}

/**
 * Pricing móvil optimizado para iOS 26
 * - Sin animaciones excesivas
 * - Mismo diseño que desktop PricingSection
 * - Usa PricingCard del desktop
 */
const MobilePricing: React.FC = () => {
  const { t } = useTranslation();

  const plans =
    (t("pricingPage.plans", { returnObjects: true }) as Plan[]) || [];

  const title = t("pricingPage.title");
  const subtitle = t("pricingPage.subtitle");
  const note = t("pricingPage.note");

  const securityBanner = t("pricingPage.securityBanner", {
    returnObjects: true,
  }) as {
    title: string;
    subtitle: string;
    features: string[];
  };

  if (!Array.isArray(plans)) {
    console.error(
      "i18n key 'pricingPage.plans' did not return an array. Check your JSON structure and i18next configuration."
    );
    return <div>Error loading pricing plans.</div>;
  }

  return (
    <section
      className="py-24 px-4 bg-white relative antialiased min-h-screen !overflow-visible"
    >
      {/* Asymmetric Torn Paper Effect Top */}
      <div
        className="absolute top-0 left-0 w-full h-16 bg-white z-20"
        style={{
          clipPath:
            "polygon(0% 0%, 100% 0%, 100% 100%, 92% 65%, 85% 90%, 78% 60%, 70% 95%, 62% 55%, 55% 90%, 48% 60%, 40% 95%, 32% 55%, 25% 90%, 18% 60%, 10% 95%, 0% 100%)",
          transform: "translateY(-98%) scaleY(-1)",
        }}
      ></div>

      <div className="text-center mb-8 relative z-10">
        <h2 className="text-3xl sm:text-4xl font-bold text-[#00342d] mb-4 tracking-tight">
          {title}
        </h2>
        <p className="text-gray-600 mt-4 max-w-2xl mx-auto text-base">
          {subtitle}
        </p>
      </div>

      {/* Contenedor principal - 3 tarjetas */}
      <div
        className="relative px-4 pb-12 !overflow-visible"
        style={{ zIndex: 20 }}
      >
        <div
          className="grid grid-cols-1 gap-6 max-w-screen-xl mx-auto !overflow-visible"
        >
          {plans.map((plan, index) => (
            <div
              key={index}
              className={`relative !overflow-visible ${
                plan.popular ? "z-10" : "z-0"
              }`}
            >
              <PricingCard plan={plan} />
            </div>
          ))}
        </div>
      </div>

      {/* Banner de Seguridad */}
      <div className="mt-12 mb-12 max-w-6xl mx-auto relative z-10 px-4">
        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-lg">
          <div className="text-center mb-6">
            <h3 className="text-xl font-bold text-[#00342d] mb-2">
              {securityBanner.title}
            </h3>
            <p className="text-gray-600 text-sm">
              {securityBanner.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3">
            {securityBanner.features.map((feature, index) => (
              <div
                key={index}
                className="flex items-start space-x-3 p-3 rounded-xl bg-gray-50 border border-gray-100"
              >
                <div className="flex-shrink-0 mt-0.5">
                  <svg
                    className="w-5 h-5 text-[#00e9be]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    strokeWidth="2.5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    ></path>
                  </svg>
                </div>
                <span className="text-gray-700 font-medium text-sm leading-relaxed">
                  {feature}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-8 text-center text-gray-500 max-w-3xl mx-auto relative z-10 px-4">
        <p className="text-sm leading-relaxed">{note}</p>
      </div>
    </section>
  );
};

export default MobilePricing;

